// KPI language/display semantics follow compliance custom-report (1509b1c15 / 17d8d0d35).
// The browser interpreter never executes JavaScript from a saved formula.
import { formulaFieldAliases, formulaFields, parseKpiFormula } from "./kpi-formula-tree"
export const KPI_DISPLAY_DEFAULT = { version: 1, layout: "standard", format: "inherit", decimals: 2, notation: "standard", currency: "USD", unit: "", percent_scale: "points", target: null }
export const KPI_COMPARISON_DEFAULT = { version: 1, mode: "previous_period", previous_dashboard_id: null, previous_widget_id: null, visual: "bars", change: "both", direction: "higher_is_better" }
export const FREE_STYLE_DEFAULT = { headers: ["", ""], rows: [["", ""], ["", ""]], columnWidths: [168, 168], columnTypes: ["text", "text"], rowTypes: ["default", "default"], cellAlign: {}, mergedCells: [], highlightHeaderRow: true, showRowNumbers: false }

export function numeric(value) {
  if (value == null || typeof value === "boolean" || String(value).trim() === "") return null
  const result = Number(String(value).replace(/,/g, ""))
  return Number.isFinite(result) ? result : null
}

export function aggregateValues(rows, field, method = "sum") {
  if (method === "count") return rows.length
  const present = rows.map((row) => row[field]).filter((value) => value != null && value !== "")
  if (method === "first") return present[0] ?? null
  if (method === "last") return present.at(-1) ?? null
  if (method === "distinct_count") return new Set(present.map(String)).size
  const values = present.map(numeric).filter((value) => value !== null)
  if (method === "sum") return values.reduce((sum, value) => sum + value, 0)
  if (!values.length) return null
  if (method === "min") return Math.min(...values)
  if (method === "max") return Math.max(...values)
  if (method === "median") { values.sort((a, b) => a - b); return (values[Math.floor(values.length / 2)] + values[Math.floor((values.length - 1) / 2)]) / 2 }
  const average = values.reduce((sum, value) => sum + value, 0) / values.length
  if (method === "avg") return average
  const variance = values.reduce((sum, value) => sum + (value - average) ** 2, 0) / values.length
  return method === "stddev" ? Math.sqrt(variance) : method === "variance" ? variance : null
}

function pythonRound(value, digits) {
  if (typeof value === "boolean") value = Number(value)
  if (!Number.isInteger(digits) || digits < 0 || digits > 6) throw new Error("Invalid rounding precision")
  if (!Number.isFinite(value)) throw new Error("Non-finite formula value")
  const buffer = new DataView(new ArrayBuffer(8))
  buffer.setFloat64(0, Math.abs(value))
  const bits = buffer.getBigUint64(0)
  const exponent = Number(bits >> 52n & 2047n)
  const significand = (bits & ((1n << 52n) - 1n)) + (exponent ? 1n << 52n : 0n)
  const power = (exponent || 1) - 1075
  const factor = 10n ** BigInt(digits)
  const numerator = significand * factor * (power >= 0 ? 1n << BigInt(power) : 1n)
  const denominator = power < 0 ? 1n << BigInt(-power) : 1n
  const quotient = numerator / denominator
  const remainder = numerator % denominator
  const rounded = quotient + (remainder * 2n > denominator || remainder * 2n === denominator && quotient % 2n === 1n ? 1n : 0n)
  const result = Number(rounded / factor) + Number(rounded % factor) / Number(factor)
  return value < 0 ? -result : result
}

export function evaluateKpiFormula(expression, table) {
  const headers = table?.headers || []
  const aliases = formulaFieldAliases(headers)
  const program = parseKpiFormula(expression)
  const resolveField = (node) => node.quoted ? node.column : aliases.get(node.column)
  const references = new Set(formulaFields(program).map(resolveField))
  if ([...references].some((name) => !headers.includes(name))) throw new Error("Unknown aggregate field")
  const cache = new Map()
  const divide = (left, right) => numeric(left) === null || numeric(right) === null || Number(right) === 0 ? null : Number(left) / Number(right)
  const run = (node) => {
    if (node.type === "number") return Number(node.value.replace(/_/g, ""))
    if (node.type === "literal") return node.value === "None" ? null : node.value === "True"
    if (node.type === "unary") { const value = run(node.operand); return node.operator === "not" ? !value : value === null ? null : node.operator === "-" ? -value : +value }
    if (node.type === "logic") return node.operator === "and" ? node.values.every((item) => Boolean(run(item))) : node.values.some((item) => Boolean(run(item)))
    if (node.type === "binary") {
      const a = run(node.left)
      const b = run(node.right)
      if (node.operator === "==") return a == b
      if (node.operator === "!=") return a != b
      if ([">", ">=", "<", "<="].includes(node.operator)) return a !== null && b !== null && ({ ">": a > b, ">=": a >= b, "<": a < b, "<=": a <= b })[node.operator]
      if (a === null || b === null) return null
      return node.operator === "+" ? Number(a) + Number(b) : node.operator === "-" ? a - b : node.operator === "*" ? a * b : divide(a, b)
    }
    const name = node.name.toLowerCase()
    if (node.type === "aggregate") {
      const field = node.column ? resolveField(node) : undefined
      const key = `${name}:${field}`
      if (!cache.has(key)) cache.set(key, aggregateValues(table?.rows || [], field, name))
      return cache.get(key)
    }
    if (name === "if_else") return run(node.args[Boolean(run(node.args[0])) ? 1 : 2])
    const args = node.args.map(run)
    if (name === "safe_divide") return divide(...args)
    if (name === "abs") return args[0] === null ? null : Math.abs(args[0])
    return args[0] === null ? null : pythonRound(args[0], args[1] ?? 0)
  }
  const value = run(program)
  if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) throw new Error("Formula must produce a finite number or blank")
  return { value, referenced_columns: [...references], row_count: table?.rows?.length || 0 }
}

export function kpiResult(widget, table) {
  if (!table) return { value: widget.kpi_raw_value ?? null, status: widget.kpi_raw_value == null ? "missing" : "ready" }
  try {
    if (widget.kpi_calculation && (widget.kpi_calculation.version !== 1 || widget.kpi_calculation.mode !== "formula")) throw new Error("Unsupported formula version")
    const result = widget.kpi_calculation ? evaluateKpiFormula(widget.kpi_calculation.expression, table)
      : { value: aggregateValues(table.rows, widget.data?.metric, widget.data?.aggregation), row_count: table.rows.length }
    return { ...result, status: result.value == null ? "missing" : "ready" }
  } catch { return { value: null, status: "error" } }
}

export function kpiPresentation(value, widget) {
  try {
    const settings = widget.kpi_display
    if (settings && (Object.keys(settings).length !== Object.keys(KPI_DISPLAY_DEFAULT).length
      || !Object.keys(KPI_DISPLAY_DEFAULT).every((key) => Object.hasOwn(settings, key))
      || settings.version !== 1 || !["standard", "compact", "prominent"].includes(settings.layout)
      || !["inherit", "number", "integer", "currency", "percent"].includes(settings.format)
      || !Number.isInteger(settings.decimals) || settings.decimals < 0 || settings.decimals > 6
      || !["standard", "compact"].includes(settings.notation) || !/^[A-Z]{3}$/.test(settings.currency)
      || !printableUnit(settings.unit) || Array.from(settings.unit).length > 24 || !["points", "ratio"].includes(settings.percent_scale)
      || settings.target !== null && (!settings.target || Object.keys(settings.target).length !== 2 || !Number.isFinite(settings.target.value) || settings.target.value <= 0 || settings.target.value > 1e15 || !["bar", "ring"].includes(settings.target.visual)))) throw new Error("Invalid KPI display settings")
    if (widget.kpi_prefix != null && (!printableUnit(widget.kpi_prefix) || Array.from(widget.kpi_prefix).length > 12)) throw new Error("Invalid KPI prefix")
    return formatKpiPresentation(value, widget)
  } catch { return { display: "—", full: "—", numeric: null, progress: null, invalid: true } }
}

function printableUnit(value) {
  if (typeof value !== "string") return false
  return Array.from(value).every((char) => {
    const point = char.codePointAt(0)
    return (char === " " || !/[\p{C}\p{Z}]/u.test(char)) && ![0x034f, 0x115f, 0x1160, 0x17b4, 0x17b5, 0x180f, 0x3164, 0xffa0].includes(point)
      && !(point >= 0x180b && point <= 0x180d || point >= 0xfe00 && point <= 0xfe0f || point >= 0xe0100 && point <= 0xe01ef)
  })
}

function formatKpiPresentation(value, widget) {
  if (typeof value === "string") return { display: value, full: value, numeric: null, progress: null }
  if (value === null || !Number.isFinite(value)) return { display: "—", full: "—", numeric: null, progress: null }
  const config = widget.kpi_display || KPI_DISPLAY_DEFAULT
  const inherited = config.format === "inherit"
  const format = inherited ? widget.style?.valueFormat || "number" : config.format
  const percent = format === "percent"
  const actual = percent && (inherited ? Math.abs(value) <= 1 : config.percent_scale === "ratio") ? value * 100 : value
  const decimals = format === "integer" ? 0 : inherited ? widget.style?.decimalPlaces ?? 2 : config.decimals
  const scales = { thousands: [1e3, "K"], millions: [1e6, "M"], billions: [1e9, "B"] }
  const scale = !percent ? scales[widget.kpi_display_scale] : null
  const currency = format === "currency"
  const unit = (config.unit || "").trim()
  const prefix = !percent && !currency ? (widget.kpi_prefix || "").trim() : ""
  const normalizedUnit = unit.toLowerCase().replace(/^in\s+/, "")
  const duplicateUnit = percent && unit === "%" || scale && [scale[1].toLowerCase(), widget.kpi_display_scale, widget.kpi_display_scale.slice(0, -1)].includes(normalizedUnit)
    || currency && (unit === config.currency || new Intl.NumberFormat("en-US", { style: "currency", currency: config.currency }).formatToParts(0).some((part) => part.type === "currency" && part.value === unit))
  const decorate = (text, suffix = "") => `${prefix && text.startsWith("-") ? "-" + prefix + text.slice(1) : prefix + text}${percent ? "%" : suffix}${unit && !duplicateUnit ? ` ${unit}` : ""}`
  const options = { ...(currency ? { style: "currency", currency: config.currency || "USD" } : {}), minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  const display = decorate(new Intl.NumberFormat("en-US", { ...options, notation: scale ? "standard" : config.notation || "standard" }).format(scale ? actual / scale[0] : actual), scale?.[1] || "")
  const full = decorate(new Intl.NumberFormat("en-US", { ...options, maximumSignificantDigits: 21 }).format(actual))
  const target = config.target
  return { display, full, numeric: actual, progress: target ? { actual: actual / target.value * 100, percent: Math.min(100, Math.max(0, actual / target.value * 100)), visual: target.visual, target: target.value } : null }
}

export function formatTableCell(value, config) {
  if (value == null) return "—"
  const number = numeric(value)
  if (!config || config.format === "auto" || config.format === "text" || number === null) return String(value)
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: config.format === "integer" ? 0 : config.decimals ?? 2, maximumFractionDigits: config.format === "integer" ? 0 : config.decimals ?? 2 }).format(number) + (config.format === "percent" ? "%" : "")
}

export function tableCellColor(row, column, rules = []) {
  for (const rule of rules) {
    if (!rule.highlight_row && rule.column !== column) continue
    const a = row[rule.column]
    const b = rule.value
    const text = String(a ?? "").toLowerCase()
    const expected = String(b ?? "").toLowerCase()
    const left = numeric(a), right = numeric(b)
    const matches = rule.operator === "equals" ? text === expected : rule.operator === "not_equals" ? text !== expected
      : rule.operator === "contains" ? text.includes(expected) : left !== null && right !== null && ({ gt: left > right, gte: left >= right, lt: left < right, lte: left <= right })[rule.operator]
    if (matches && /^#[\da-f]{6}$/i.test(rule.color)) return rule.color
  }
  return undefined
}
